import React, { useContext } from "react";
import ShareSocialPopup from "../../ShareSocialPopup";
import PublishingOptions from "../../PublishingOptions";
import ConfirmationPopup from "../../EventsDashboard/ConfirmationPopup";
import NewAlbumModal from "./NewAlbumModal";
import CreateFolderModal from "./CreateModals/CreateFolderModal";
import CreateEventModal from "./CreateModals/CreateEventModal";
import Rename from "./FolderActions/Rename";
import Delete from "./FolderActions/Delete";
import FolderFeatures from "./FolderFeatures";
import DeleteShortcut from "./FolderFeatures/DeleteShortcut";
import MoveToFolderPopup from "./MoveToFolderPopup";
import RenameEvent from "./EventActions/RenameEvent";
import DeleteEvent from "./EventActions/DeleteEvent";
import ManageContentPermissionsModal from "./ManageContentPermissionsModal";
import { PhotoManagerContext } from "./index";
import { EventType } from "./types";

interface PhotoManagerModalsProps {
  // Only callback props that contain business logic not in context
  findEventById: (eventId: number, list: EventType[]) => EventType | undefined;
  selectedEventFromEventList: EventType | undefined;
  handleCreateEventSuccess: (newEvent: EventType, folderId: number | null) => void;
  handleCloseFolderModal: () => void;
  handleCloseEventModal: () => void;
  onReNameClicked: (folder: any, name: string) => Promise<void>;
  onDeleteFolder: (folder: any) => Promise<void>;
  addShortcutToTree: (shortcutData: {
    id: number;
    name: string;
    parentFolderId: number | null;
    referenceId: number;
    type: "LibraryFolder" | "Event";
  }) => void;
  onShortcutDelete: (shortCut: any) => Promise<void>;
  onMovingSuccess: (val: {
    shouldReloadTreeView: boolean;
    src: any;
    destination: any;
  }) => void;
  onRenameEvent: (event: EventType, newName: string) => Promise<void>;
  onDeleteEvent: (event: EventType) => Promise<void>;
  onCloseFolderFeatureModal: () => void;
  onCloseDeleteShortcutModal: () => void;
}

const PhotoManagerModals: React.FC<PhotoManagerModalsProps> = ({
  findEventById,
  selectedEventFromEventList,
  handleCreateEventSuccess,
  handleCloseFolderModal,
  handleCloseEventModal,
  onReNameClicked,
  onDeleteFolder,
  addShortcutToTree,
  onShortcutDelete,
  onMovingSuccess,
  onRenameEvent,
  onDeleteEvent,
  onCloseFolderFeatureModal,
  onCloseDeleteShortcutModal
}) => {
  const context = useContext(PhotoManagerContext);

  if (!context) return null;

  const {
    activeShareModalEventId,
    eventsView,
    markUploadPopupId,
    selectedFolderData,
    updateEventList,
    confirmationLoader,
    privacyPopupId,
    selectedEvent,
    updatePrivacy,
    schoolUser,
    publicSharingDisabled,
    eventPasswordsDisabled,
    showNewAlbumModal,
    onCreateAlbumSuccess,
    showCreateFolderModal,
    createFolderType,
    selectedFolderId,
    isHeaderFolderCreation,
    showCreateEventModal,
    isHeaderEventCreation,
    showRenameModal,
    selectedFolderActions,
    showDeleteModal,
    showFolderFeatureModal,
    sourceEntityId,
    sourceEntityName,
    sourceEntityType,
    sourceParentFolderId,
    showDeleteShortcutModal,
    sourceShortCut,
    showMovePopup,
    sourceEntityToMove,
    closeMovePopup,
    showRenameEventModal,
    selectedEventForAction,
    setShowRenameEventModal,
    showDeleteEventModal,
    setShowDeleteEventModal,
    showManagePermissionsModal,
    permissionsModalContent,
    toggleManagePermissionsModal,
    // Modal control methods from context
    setActiveShareModalEventId,
    setMarkUploadPopupId,
    setPrivacyPopupId,
    setShowNewAlbumModal,
    setShowRenameModal,
    setShowDeleteModal,
    closeRenameModal,
    closeDeleteModal
  } = context;

  const findEvent = (eventId: number): EventType | undefined => {
    return (
      findEventById(eventId, eventsView) ||
      findEventById(eventId, selectedFolderData?.events || [])
    );
  };

  return (
    <>
      {activeShareModalEventId !== null && (
        <div className="event-share-modal">
          <ShareSocialPopup
            galleryURL={
              findEvent(activeShareModalEventId)?.browsePhotosURL || ""
            }
            hideShareGalleryModal={() => setActiveShareModalEventId(null)}
            text="Share with participants or just show off ;)"
          />
        </div>
      )}
      {markUploadPopupId && (
        <ConfirmationPopup
          closePopup={() => setMarkUploadPopupId(null)}
          popupId={findEvent(markUploadPopupId)?.markUploadCompleteURL || ""}
          eventId={markUploadPopupId}
          onSuccess={() => updateEventList()}
          loader={confirmationLoader}
        />
      )}
      {privacyPopupId &&
        (() => {
          const eventData = findEvent(privacyPopupId) || selectedEvent;

          if (!eventData) return null;

          return (
            <PublishingOptions
              closePopup={() => setPrivacyPopupId(null)}
              eventData={{
                id: eventData.id,
                name: eventData.name,
                url: eventData.browsePhotosURL,
                publishMode: eventData.publishMode,
                password: eventData.password
              }}
              updateEventPrivacy={updatePrivacy}
              schoolUser={schoolUser}
              publicSharingDisabled={publicSharingDisabled}
              eventPasswordsDisabled={eventPasswordsDisabled}
              eventDataURL={eventData.browsePhotosURL || ""}
            />
          );
        })()}
      {showNewAlbumModal && (
        <NewAlbumModal
          eventId={
            showNewAlbumModal ||
            selectedEventFromEventList?.id ||
            selectedEvent?.id ||
            0
          }
          onClose={() => setShowNewAlbumModal(null)}
          onCreateAlbumSuccess={(newAlbum) => {
            onCreateAlbumSuccess(newAlbum);
            setShowNewAlbumModal(null);
          }}
        />
      )}
      {showCreateFolderModal && (
        <CreateFolderModal
          type={createFolderType}
          onClose={handleCloseFolderModal}
          parentFolderId={selectedFolderId || null}
          forceRootLevel={isHeaderFolderCreation}
        />
      )}
      {showCreateEventModal && (
        <CreateEventModal
          onClose={handleCloseEventModal}
          onSuccess={handleCreateEventSuccess}
          folderId={
            isHeaderEventCreation ? null : selectedFolderId || null
          }
        />
      )}
      {showRenameModal && selectedFolderActions && (
        <Rename
          onClose={closeRenameModal}
          folder={selectedFolderActions}
          onReNameClicked={onReNameClicked}
        />
      )}
      {showDeleteModal && selectedFolderActions && (
        <Delete
          onClose={closeDeleteModal}
          folder={selectedFolderActions}
          onDeleteFolder={onDeleteFolder}
        />
      )}
      {showFolderFeatureModal && (
        <FolderFeatures
          onClose={onCloseFolderFeatureModal}
          onSuccess={shortcutData => {
            addShortcutToTree(shortcutData);
          }}
          sourceEntityId={sourceEntityId}
          sourceEntityName={sourceEntityName}
          sourceEntityType={sourceEntityType}
          sourceParentFolderId={sourceParentFolderId}
        />
      )}
      {showDeleteShortcutModal && sourceShortCut?.referenceId && (
        <DeleteShortcut
          onClose={onCloseDeleteShortcutModal}
          shortCut={sourceShortCut}
          onDeleteShortCut={onShortcutDelete}
          type="folder"
          referenceId={sourceShortCut.referenceId}
        />
      )}
      {showMovePopup && sourceEntityToMove && (
        <MoveToFolderPopup
          onClose={closeMovePopup}
          srcEntity={sourceEntityToMove}
          onMovingSuccess={onMovingSuccess}
        />
      )}
      {showRenameEventModal && selectedEventForAction && (
        <RenameEvent
          onClose={() => setShowRenameEventModal(null)}
          event={selectedEventForAction}
          onRenameClicked={onRenameEvent}
        />
      )}
      {showDeleteEventModal && selectedEventForAction && (
        <DeleteEvent
          onClose={() => setShowDeleteEventModal(null)}
          event={selectedEventForAction}
          onDeleteEvent={onDeleteEvent}
        />
      )}
      {showManagePermissionsModal && permissionsModalContent && (
        <ManageContentPermissionsModal
          contentType={permissionsModalContent.contentType}
          contentId={permissionsModalContent.contentId}
          contentName={permissionsModalContent.contentName}
          onClose={() => toggleManagePermissionsModal(false)}
        />
      )}
    </>
  );
};

export default PhotoManagerModals;
